import { useQuery } from "@tanstack/react-query";
import {
  Cable,
  Globe,
  RefreshCw,
  ShieldCheck,
  TerminalSquare,
} from "lucide-react";

import MetricCard from "@/components/layout/MetricCard";
import PageHeader from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { api } from "@/lib/api";

function EndpointList({
  title,
  description,
  items,
}: {
  title: string;
  description: string;
  items: Array<{ label: string; value: string }>;
}) {
  return (
    <Card className="border-border/80 bg-card/80">
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {items.map((item) => (
          <div key={item.label} className="rounded-2xl border border-border/80 bg-muted/20 p-3">
            <p className="font-medium">{item.label}</p>
            <p className="mt-1 break-all text-muted-foreground">{item.value}</p>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

export default function SettingsPage() {
  const runtimeQuery = useQuery({
    queryKey: ["runtime-settings"],
    queryFn: api.getRuntimeSettings,
  });

  if (runtimeQuery.isLoading) {
    return <div className="text-sm text-muted-foreground">Loading settings...</div>;
  }

  if (runtimeQuery.isError || !runtimeQuery.data) {
    return <div className="text-sm text-red-500">Không tải được runtime settings.</div>;
  }

  const runtime = runtimeQuery.data;

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Runtime"
        title="Runtime settings"
        description="Hiển thị trực tiếp các endpoint, đường dẫn và trạng thái backend mà frontend đang dựa vào."
        actions={
          <Button
            variant="outline"
            onClick={() => {
              void runtimeQuery.refetch();
            }}
            disabled={runtimeQuery.isFetching}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${runtimeQuery.isFetching ? "animate-spin" : ""}`} />
            Reload
          </Button>
        }
      />

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          label="Render source"
          value={runtime.renderReady ? "Ready" : "Missing"}
          hint={runtime.renderSourceDir}
          icon={<Globe className="h-4 w-4" />}
        />
        <MetricCard
          label="Event logs"
          value={runtime.eventLogsReady ? "Ready" : "Missing"}
          hint={runtime.eventLogEventsUrl}
          icon={<Cable className="h-4 w-4" />}
        />
        <MetricCard
          label="App secret"
          value={runtime.secretConfigured ? "Configured" : "Missing"}
          hint="Ảnh hưởng tới upload pipeline cũ."
          icon={<ShieldCheck className="h-4 w-4" />}
        />
        <MetricCard
          label="Event log secret"
          value={runtime.eventLogSecretConfigured ? "Configured" : "Missing"}
          hint={runtime.sourceOfTruth}
          icon={<TerminalSquare className="h-4 w-4" />}
        />
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <EndpointList
          title="Core runtime"
          description="Những giá trị backend mà frontend và pipeline đang dùng trực tiếp."
          items={[
            { label: "Base URL", value: runtime.baseUrl },
            { label: "Upload directory", value: runtime.uploadDir },
            { label: "Render source directory", value: runtime.renderSourceDir },
            { label: "Source of truth", value: runtime.sourceOfTruth },
          ]}
        />
        <EndpointList
          title="Asset library"
          description="Public endpoints cho clothes library và static assets."
          items={[
            { label: "Clothes meta", value: runtime.assetLibraryClothesMetaUrl },
            { label: "Clothes items", value: runtime.assetLibraryClothesItemsUrl },
            { label: "Clothes item detail", value: runtime.assetLibraryClothesItemUrl },
            { label: "Render files", value: runtime.renderStaticUrl },
            { label: "Preview files", value: runtime.renderPreviewStaticUrl || "N/A" },
          ]}
        />
      </div>

      <EndpointList
        title="Event log endpoints"
        description="Những endpoint hiện tại cho ingest, list, overview, breakdown và series."
        items={[
          { label: "Ingest URL", value: runtime.eventLogIngestUrl },
          { label: "Events API", value: runtime.eventLogEventsUrl },
          { label: "Overview API", value: runtime.eventLogOverviewUrl },
          { label: "Breakdown API", value: runtime.eventLogBreakdownUrl },
          { label: "Series API", value: runtime.eventLogSeriesUrl },
        ]}
      />

      <div className="grid gap-6 xl:grid-cols-2">
        <Card className="border-border/80 bg-card/80">
          <CardHeader>
            <CardTitle>Upload contract</CardTitle>
            <CardDescription>
              Snippet tham chiếu cho pipeline screenshot cũ.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <pre className="overflow-x-auto rounded-2xl border border-border/80 bg-black/25 p-4 text-xs leading-6 text-muted-foreground">
              <code>{`exports['screenshot-basic']:requestScreenshotUpload(
  '${runtime.uploadUrl}?rmbg=true&name=' .. name,
  'file',
  {
    headers = {
      Secret = '<APP_SECRET>',
    },
    encoding = 'png'
  },
  function(data)
    local resp = json.decode(data)
  end
)`}</code>
            </pre>
          </CardContent>
        </Card>

        <Card className="border-border/80 bg-card/80">
          <CardHeader>
            <CardTitle>Event log contract</CardTitle>
            <CardDescription>
              Payload mẫu để đẩy event stream từ FiveM vào ingest API.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <pre className="overflow-x-auto rounded-2xl border border-border/80 bg-black/25 p-4 text-xs leading-6 text-muted-foreground">
              <code>{`PerformHttpRequest('${runtime.eventLogIngestUrl}', function(statusCode, body)
  print(statusCode, body)
end, 'POST', json.encode({
  events = {
    {
      source = 'fivem',
      category = 'money',
      action = 'transfer',
      eventName = 'money.transfer',
      labels = {
        shard = 'main-1',
        resource = 'qb-banking',
        currency = 'cash'
      },
      actor = {
        type = 'player',
        id = senderIdentifier,
        label = senderName
      },
      target = {
        type = 'player',
        id = receiverIdentifier,
        label = receiverName
      },
      amount = amount,
      currency = 'cash',
      status = 'success',
      resource = 'qb-banking',
      occurredAt = os.date('!%Y-%m-%dT%H:%M:%SZ'),
      metadata = {
        characterId = senderCid,
        reason = reason
      }
    }
  }
}), {
  ['Content-Type'] = 'application/json',
  ['Secret'] = '<APP_EVENT_LOG_SECRET>'
})`}</code>
            </pre>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
